// M2 Ninja Slice — wave hand to slice fruit, miss bombs.
// Spec: /microgames/02-ninja-slice.md
// Lifts from fruit-ninja-tablet SliceDetector.kt / FruitPhysics.kt.

import type {
  GameContext,
  HandFrame,
  InputFrame,
  Microgame,
  MicrogameResult,
  Point,
} from '@pose-game/core';

const DURATION_SEC = 22;
const TRAIL_LEN = 12;
const SLICE_VELOCITY_THRESHOLD = 500; // px/s — easier to slice
const GRAVITY = 1100; // px/s^2 — slower fall arc
const SPAWN_INTERVAL = 1.5; // seconds between single spawns
const CLUSTER_INTERVAL = 5.5; // seconds between cluster spawns
const CLUSTER_SIZE = 3;
const WIN_SLICES = 8;
const FRUIT_RADIUS = 56;

type FruitKind = 'normal' | 'double' | 'bomb';

type SpriteKind =
  | 'apple'
  | 'cherry'
  | 'banana'
  | 'watermelon'
  | 'strawberry'
  | 'peach'
  | 'pineapple'
  | 'grape'
  | 'bomb';

interface Fruit {
  id: number;
  kind: FruitKind;
  color: string;
  sprite: SpriteKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  angularVel: number;
  sliced: boolean;
  /** When sliced, halves spawn; the original fruit becomes inactive. */
  active: boolean;
}

interface Half {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  angularVel: number;
  color: string;
  side: -1 | 1;
  ageSec: number;
}

interface WristState {
  trail: Point[];
  visible: boolean;
}

const NORMAL_COLORS = [
  '#ef4444', // red apple
  '#f59e0b', // orange
  '#fbbf24', // yellow / pineapple-ish
  '#10b981', // green
  '#ec4899', // pink strawberry
  '#a855f7', // purple grape
];

const DOUBLE_COLOR = '#fde68a'; // gold double
const BOMB_COLOR = '#111827';

// chomp-ios clay fruit sprites (copied to /public/assets/fruits/<kind>.png).
const NORMAL_SPRITES: ReadonlyArray<SpriteKind> = [
  'apple', 'cherry', 'banana', 'watermelon', 'strawberry', 'peach', 'grape',
];
const SPRITE_DOMINANT_COLOR: Record<SpriteKind, string> = {
  apple: '#e63946',
  cherry: '#ff5d8f',
  banana: '#f4d35e',
  watermelon: '#3cb371',
  strawberry: '#ff6b9a',
  peach: '#ffa07a',
  pineapple: '#f1c40f',
  grape: '#8e44ad',
  bomb: '#1a1a1a',
};
const FRUIT_IMAGES: Record<SpriteKind, HTMLImageElement> = (() => {
  const out = {} as Record<SpriteKind, HTMLImageElement>;
  for (const k of [...NORMAL_SPRITES, 'pineapple' as const, 'bomb' as const]) {
    const img = new Image();
    img.src = `/assets/fruits/${k}.png`;
    out[k] = img;
  }
  return out;
})();

export const ninjaSlice: Microgame = (() => {
  let game: GameContext | null = null;
  let elapsed = 0;
  let lastSpawnAt = 0;
  let lastClusterAt = 0;
  let nextId = 1;

  const fruits: Fruit[] = [];
  const halves: Half[] = [];

  const leftWrist: WristState = { trail: [], visible: false };
  const rightWrist: WristState = { trail: [], visible: false };

  let slices = 0;
  let bombsHit = 0;
  let failed = false;
  let canvasW = 0;
  let canvasH = 0;

  const rand = (): number => (game?.rng ? game.rng() : Math.random());

  const pickNormalColor = (): string => {
    const idx = Math.floor(rand() * NORMAL_COLORS.length);
    return NORMAL_COLORS[idx] ?? '#ef4444';
  };

  const pickKind = (): FruitKind => {
    const r = rand();
    if (r < 0.05) return 'bomb';
    if (r < 0.15) return 'double';
    return 'normal';
  };

  const colorForKind = (kind: FruitKind): string => {
    if (kind === 'bomb') return BOMB_COLOR;
    if (kind === 'double') return DOUBLE_COLOR;
    return pickNormalColor();
  };

  const spriteForKind = (kind: FruitKind): SpriteKind => {
    if (kind === 'bomb') return 'bomb';
    if (kind === 'double') return 'pineapple'; // golden "double" = pineapple
    const idx = Math.floor(rand() * NORMAL_SPRITES.length);
    return NORMAL_SPRITES[idx] ?? 'apple';
  };

  const spawnFruit = (): void => {
    if (canvasW <= 0 || canvasH <= 0) return;
    // Pick side: left or right of screen
    const side: -1 | 1 = rand() < 0.5 ? -1 : 1;
    const margin = 60;
    const startX = side === -1 ? margin : canvasW - margin;
    const startY = canvasH + 40;

    // Aim toward an apex in the upper third, on the opposite half-ish.
    const targetX =
      side === -1
        ? canvasW * (0.45 + rand() * 0.45) // arcing rightward
        : canvasW * (0.1 + rand() * 0.45); // arcing leftward
    const travelTime = 1.4 + rand() * 0.7;
    const vx = (targetX - startX) / travelTime;
    // initial vy chosen so apex reaches ~upper third
    const apexHeight = canvasH * (0.55 + rand() * 0.25);
    const vy = -(GRAVITY * travelTime * 0.5 + apexHeight / travelTime);

    const kind = pickKind();
    const sprite = spriteForKind(kind);
    const fruit: Fruit = {
      id: nextId++,
      kind,
      color: kind === 'normal' ? SPRITE_DOMINANT_COLOR[sprite] : colorForKind(kind),
      sprite,
      x: startX,
      y: startY,
      vx,
      vy,
      rotation: rand() * Math.PI * 2,
      angularVel: (rand() - 0.5) * 6,
      sliced: false,
      active: true,
    };
    fruits.push(fruit);
  };

  const spawnCluster = (): void => {
    for (let i = 0; i < CLUSTER_SIZE; i++) spawnFruit();
  };

  const updateTrail = (wrist: WristState, hand: HandFrame): void => {
    if (!hand.visible || hand.landmarks.length === 0) {
      wrist.visible = false;
      // let the trail fade by not adding new points; clear quickly when gone
      if (wrist.trail.length > 0) wrist.trail.shift();
      return;
    }
    const head = hand.landmarks[0];
    if (!head) {
      wrist.visible = false;
      if (wrist.trail.length > 0) wrist.trail.shift();
      return;
    }
    wrist.visible = true;
    wrist.trail.push({ x: head.x, y: head.y });
    while (wrist.trail.length > TRAIL_LEN) wrist.trail.shift();
  };

  const wristSpeed = (hand: HandFrame): number => {
    const v = hand.wristVelocity;
    return Math.hypot(v.x, v.y);
  };

  const segmentHitsCircle = (
    ax: number,
    ay: number,
    bx: number,
    by: number,
    cx: number,
    cy: number,
    r: number,
  ): boolean => {
    const dx = bx - ax;
    const dy = by - ay;
    const lenSq = dx * dx + dy * dy;
    if (lenSq < 0.0001) {
      const ddx = cx - ax;
      const ddy = cy - ay;
      return ddx * ddx + ddy * ddy <= r * r;
    }
    let t = ((cx - ax) * dx + (cy - ay) * dy) / lenSq;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    const px = ax + dx * t;
    const py = ay + dy * t;
    const ex = cx - px;
    const ey = cy - py;
    return ex * ex + ey * ey <= r * r;
  };

  const sliceFruit = (fruit: Fruit, segDx: number, segDy: number): void => {
    fruit.sliced = true;
    fruit.active = false;
    // Split into two halves, perpendicular to slice direction.
    const segLen = Math.hypot(segDx, segDy) || 1;
    const nx = -segDy / segLen;
    const ny = segDx / segLen;
    const burst = 280;
    halves.push({
      x: fruit.x,
      y: fruit.y,
      vx: fruit.vx + nx * burst,
      vy: fruit.vy + ny * burst,
      rotation: fruit.rotation,
      angularVel: fruit.angularVel + 6,
      color: fruit.color,
      side: -1,
      ageSec: 0,
    });
    halves.push({
      x: fruit.x,
      y: fruit.y,
      vx: fruit.vx - nx * burst,
      vy: fruit.vy - ny * burst,
      rotation: fruit.rotation,
      angularVel: fruit.angularVel - 6,
      color: fruit.color,
      side: 1,
      ageSec: 0,
    });
  };

  const tryDetectSlices = (hand: HandFrame, wrist: WristState): void => {
    if (failed) return;
    if (!wrist.visible || wrist.trail.length < 2) return;
    if (wristSpeed(hand) < SLICE_VELOCITY_THRESHOLD) return;

    // Use the last two trail points as the active slice segment.
    const b = wrist.trail[wrist.trail.length - 1];
    const a = wrist.trail[wrist.trail.length - 2];
    if (!a || !b) return;
    const segDx = b.x - a.x;
    const segDy = b.y - a.y;

    for (const fruit of fruits) {
      if (!fruit.active || fruit.sliced) continue;
      if (segmentHitsCircle(a.x, a.y, b.x, b.y, fruit.x, fruit.y, FRUIT_RADIUS)) {
        if (fruit.kind === 'bomb') {
          bombsHit++;
          failed = true;
          sliceFruit(fruit, segDx, segDy);
          game?.audio.thud();
          return;
        }
        sliceFruit(fruit, segDx, segDy);
        slices += fruit.kind === 'double' ? 3 : 1;
        game?.audio.pop();
      }
    }
  };

  const stepFruits = (dt: number): void => {
    for (const f of fruits) {
      if (!f.active) continue;
      f.vy += GRAVITY * dt;
      f.x += f.vx * dt;
      f.y += f.vy * dt;
      f.rotation += f.angularVel * dt;
    }
    // prune offscreen / inactive
    for (let i = fruits.length - 1; i >= 0; i--) {
      const f = fruits[i];
      if (!f) continue;
      const offscreen =
        f.y - FRUIT_RADIUS > canvasH + 200 ||
        f.x < -300 ||
        f.x > canvasW + 300;
      if (!f.active || offscreen) fruits.splice(i, 1);
    }
  };

  const stepHalves = (dt: number): void => {
    for (const h of halves) {
      h.vy += GRAVITY * dt;
      h.x += h.vx * dt;
      h.y += h.vy * dt;
      h.rotation += h.angularVel * dt;
      h.ageSec += dt;
    }
    for (let i = halves.length - 1; i >= 0; i--) {
      const h = halves[i];
      if (!h) continue;
      if (h.y > canvasH + 200 || h.ageSec > 3) halves.splice(i, 1);
    }
  };

  const drawBackground = (g: CanvasRenderingContext2D): void => {
    const grad = g.createLinearGradient(0, 0, 0, canvasH);
    grad.addColorStop(0, '#0b1020');
    grad.addColorStop(1, '#1b1136');
    g.fillStyle = grad;
    g.fillRect(0, 0, canvasW, canvasH);
  };

  const drawFruit = (g: CanvasRenderingContext2D, f: Fruit): void => {
    g.save();
    g.translate(f.x, f.y);
    g.rotate(f.rotation);
    const img = FRUIT_IMAGES[f.sprite];
    if (img != null && img.complete && img.naturalWidth > 0) {
      // Sprite art is bigger than the strict slice hitbox — matches chomp feel.
      const d = FRUIT_RADIUS * 2.6;
      g.drawImage(img, -d / 2, -d / 2, d, d);
      if (f.kind === 'double') {
        // gold halo for the double-point fruit
        g.strokeStyle = '#fff7c2';
        g.lineWidth = 5;
        g.beginPath();
        g.arc(0, 0, FRUIT_RADIUS * 1.05, 0, Math.PI * 2);
        g.stroke();
      }
    } else if (f.kind === 'bomb') {
      // Fallback path (sprite not yet decoded).
      g.fillStyle = BOMB_COLOR;
      g.beginPath();
      g.arc(0, 0, FRUIT_RADIUS, 0, Math.PI * 2);
      g.fill();
      g.strokeStyle = '#ef4444';
      g.lineWidth = 6;
      const r = FRUIT_RADIUS * 0.55;
      g.beginPath();
      g.moveTo(-r, -r);
      g.lineTo(r, r);
      g.moveTo(r, -r);
      g.lineTo(-r, r);
      g.stroke();
    } else {
      g.fillStyle = f.color;
      g.beginPath();
      g.arc(0, 0, FRUIT_RADIUS, 0, Math.PI * 2);
      g.fill();
    }
    g.restore();
  };

  const drawHalf = (g: CanvasRenderingContext2D, h: Half): void => {
    g.save();
    g.translate(h.x, h.y);
    g.rotate(h.rotation);
    const alpha = Math.max(0, 1 - h.ageSec / 3);
    g.globalAlpha = alpha;
    g.fillStyle = h.color;
    g.beginPath();
    if (h.side === -1) {
      g.arc(0, 0, FRUIT_RADIUS, Math.PI / 2, (Math.PI * 3) / 2);
    } else {
      g.arc(0, 0, FRUIT_RADIUS, -Math.PI / 2, Math.PI / 2);
    }
    g.closePath();
    g.fill();
    g.restore();
  };

  const drawTrail = (
    g: CanvasRenderingContext2D,
    wrist: WristState,
    color: string,
  ): void => {
    const trail = wrist.trail;
    if (trail.length < 2) {
      if (wrist.visible && trail.length === 1) {
        const p = trail[0];
        if (p) {
          g.fillStyle = color;
          g.beginPath();
          g.arc(p.x, p.y, 8, 0, Math.PI * 2);
          g.fill();
        }
      }
      return;
    }
    // Draw segments fading: oldest=low alpha, newest=full.
    g.save();
    g.strokeStyle = color;
    g.lineCap = 'round';
    for (let i = 1; i < trail.length; i++) {
      const a = trail[i - 1];
      const b = trail[i];
      if (!a || !b) continue;
      const t = i / (trail.length - 1); // 0 oldest..1 newest
      g.globalAlpha = 0.1 + 0.9 * t;
      g.lineWidth = 4 + 10 * t;
      g.beginPath();
      g.moveTo(a.x, a.y);
      g.lineTo(b.x, b.y);
      g.stroke();
    }
    g.restore();
    // wrist dot at current position
    const head = trail[trail.length - 1];
    if (head && wrist.visible) {
      g.fillStyle = color;
      g.beginPath();
      g.arc(head.x, head.y, 10, 0, Math.PI * 2);
      g.fill();
    }
  };

  const drawHud = (g: CanvasRenderingContext2D): void => {
    g.fillStyle = '#fff';
    g.textAlign = 'left';
    g.textBaseline = 'top';
    const fs = Math.max(18, Math.round(canvasH * 0.04));
    g.font = `bold ${fs}px system-ui, sans-serif`;
    g.fillText(`slices: ${slices}`, 20, 18);
    const remain = Math.max(0, DURATION_SEC - elapsed);
    g.fillStyle = '#cbd5e1';
    g.font = `${Math.round(fs * 0.7)}px system-ui, sans-serif`;
    g.fillText(`${remain.toFixed(1)}s`, 20, 18 + fs + 4);
    if (failed) {
      g.fillStyle = '#ef4444';
      g.textAlign = 'center';
      g.font = `bold ${Math.round(fs * 1.4)}px system-ui, sans-serif`;
      g.fillText('BOMB!', canvasW / 2, canvasH / 2 - fs);
    }
  };

  return {
    name: '02-ninja-slice',
    durationSec: DURATION_SEC,
    requires: { hands: true },

    init(c: GameContext): void {
      game = c;
      elapsed = 0;
      lastSpawnAt = 0;
      lastClusterAt = 0;
      nextId = 1;
      fruits.length = 0;
      halves.length = 0;
      leftWrist.trail.length = 0;
      leftWrist.visible = false;
      rightWrist.trail.length = 0;
      rightWrist.visible = false;
      slices = 0;
      bombsHit = 0;
      failed = false;
      canvasW = c.canvas.width;
      canvasH = c.canvas.height;
    },

    tick(dt: number, frame: InputFrame): void {
      if (game == null) return;
      canvasW = game.canvas.width;
      canvasH = game.canvas.height;
      elapsed += dt;

      updateTrail(leftWrist, frame.hands.left);
      updateTrail(rightWrist, frame.hands.right);

      if (!failed) {
        tryDetectSlices(frame.hands.left, leftWrist);
        tryDetectSlices(frame.hands.right, rightWrist);

        // Spawn schedule (only while the round is still live).
        if (elapsed < DURATION_SEC) {
          if (elapsed - lastSpawnAt >= SPAWN_INTERVAL) {
            spawnFruit();
            lastSpawnAt = elapsed;
          }
          if (elapsed - lastClusterAt >= CLUSTER_INTERVAL) {
            spawnCluster();
            lastClusterAt = elapsed;
          }
        }
      }

      stepFruits(dt);
      stepHalves(dt);
    },

    render(g: CanvasRenderingContext2D): void {
      if (game == null) return;
      drawBackground(g);
      for (const f of fruits) if (f.active) drawFruit(g, f);
      for (const h of halves) drawHalf(g, h);
      drawTrail(g, leftWrist, '#22d3ee'); // cyan left
      drawTrail(g, rightWrist, '#e879f9'); // magenta right
      drawHud(g);
    },

    end(): MicrogameResult {
      const won = !failed && slices >= WIN_SLICES;
      return {
        microgame: '02-ninja-slice',
        won,
        score: slices,
        detail: { bombsHit },
      };
    },
  };
})();
